import { morph, MORPH_OPTIONS } from "@theme/morph";
import { createReporter } from "@theme/logger";

const reportError = createReporter("SectionRenderer");

const SUPERSEDED_SECTION_RENDER = "Section render superseded";

/**
 * @modifier Mysa
 * When a live `[data-morph-island]` is present, park it (owner memory state),
 * morph the section, then reinsert using `data-morph-insert-before`. Reinsert
 * is in `finally` so a throwing `morph()` cannot leave the island detached.
 */

/**
 * A class to re-render sections using the Section Rendering API
 */
class SectionRenderer {
  /**
   * The cache of section HTML
   * @type {Map<string, string>}
   */
  #cache = new Map();

  /**
   * The abort controllers by section ID
   * @type {Map<string, AbortController>}
   */
  #abortControllersBySectionId = new Map();

  /**
   * The pending promises
   * @type {Map<string, Promise<string>>}
   */
  #pendingPromises = new Map();

  constructor() {
    window.addEventListener("load", this.#cachePageSections.bind(this));
  }

  /**
   * Renders a section
   * @param {string} sectionId - The section ID
   * @param {Object} [options] - The options
   * @param {boolean} [options.cache] - Whether to use the cache
   * @param {URL} [options.url] - The URL to render the section from
   * @returns {Promise<string | null>} The rendered section HTML, or null when a later render cancelled this one
   */
  async renderSection(sectionId, options) {
    const { cache = !Shopify.designMode } = options ?? {};
    const { url } = options ?? {};
    const previousAbortController =
      this.#abortControllersBySectionId.get(sectionId);

    const abortController = new AbortController();
    this.#abortControllersBySectionId.set(sectionId, abortController);

    const renderPromise = this.#renderSection(
      sectionId,
      { cache, url },
      abortController,
    );

    previousAbortController?.abort(
      new DOMException(SUPERSEDED_SECTION_RENDER, "AbortError"),
    );

    return renderPromise;
  }

  /**
   * Renders a section with an abort controller.
   * @param {string} sectionId
   * @param {{ cache: boolean, url?: URL }} options
   * @param {AbortController} abortController
   * @returns {Promise<string | null>}
   */
  async #renderSection(sectionId, { cache, url }, abortController) {
    let sectionHTML = "";

    try {
      sectionHTML = await this.getSectionHTML(
        sectionId,
        cache,
        url,
        abortController.signal,
      );
    } catch (error) {
      if (abortController.signal.aborted) return null;

      throw error;
    } finally {
      if (
        this.#abortControllersBySectionId.get(sectionId) === abortController
      ) {
        this.#abortControllersBySectionId.delete(sectionId);
      }
    }

    if (abortController.signal.aborted) return null;

    morphSection(sectionId, sectionHTML);
    return sectionHTML;
  }

  /**
   * Gets the HTML for a section
   * @param {string} sectionId - The section ID
   * @param {boolean} useCache - Whether to use the cache
   * @param {URL} url - The URL to render the section for
   * @param {AbortSignal} [signal] - Cancels the section render fetch
   * @returns {Promise<string>} The rendered section HTML
   *
   * Always delete the in-flight promise from `#pendingPromises`, including
   * when `fetch` rejects. Leaving a rejected promise in the map would make
   * later refreshes of the same URL fail immediately with no retry.
   * Related: GH issue #654 / SHOPIFY-THEME-DB
   */
  async getSectionHTML(
    sectionId,
    useCache = true,
    url = new URL(window.location.href),
    signal,
  ) {
    const sectionUrl = buildSectionRenderingURL(sectionId, url);
    const shouldSharePendingPromise = !signal;
    const pendingPromise = shouldSharePendingPromise
      ? this.#pendingPromises.get(sectionUrl)
      : undefined;
    if (pendingPromise) return pendingPromise;

    if (useCache) {
      const cachedHTML = this.#cache.get(sectionUrl);

      if (cachedHTML) return cachedHTML;
    }

    const request = this.#fetchSectionHTML(sectionUrl, signal);

    if (shouldSharePendingPromise) {
      this.#pendingPromises.set(sectionUrl, request);
    }

    try {
      const sectionHTML = await request;
      this.#cache.set(sectionUrl, sectionHTML);
      return sectionHTML;
    } finally {
      this.#pendingPromises.delete(sectionUrl);
    }
  }

  /**
   * Fetches section HTML. Awaiting fetch here keeps the abort rejection on
   * this promise so `#renderSection` can return null instead of leaking it.
   * @param {string} sectionUrl
   * @param {AbortSignal} [signal]
   * @returns {Promise<string>}
   */
  async #fetchSectionHTML(sectionUrl, signal) {
    const response = await fetch(sectionUrl, signal ? { signal } : undefined);
    return response.text();
  }

  /**
   * Caches the page sections
   */
  #cachePageSections() {
    for (const section of document.querySelectorAll(".shopify-section")) {
      const url = buildSectionRenderingURL(section.id);
      if (this.#cache.get(url)) return;
      if (containsShadowRoot(section)) return;

      this.#cache.set(url, section.outerHTML);
    }
  }
}

const SECTION_ID_PREFIX = "shopify-section-";

/**
 * Builds a section rendering URL
 * @param {string} sectionId - The section ID
 * @param {URL} url - The URL to render the section for
 * @returns {string} The section rendering URL
 */
function buildSectionRenderingURL(
  sectionId,
  url = new URL(window.location.href),
) {
  url.searchParams.set("section_id", normalizeSectionId(sectionId));
  url.searchParams.sort();

  return url.toString();
}

/**
 * Builds a section selector
 * @param {string} sectionId - The section ID
 * @returns {string} The section selector
 */
export function buildSectionSelector(sectionId) {
  return `${SECTION_ID_PREFIX}${sectionId}`;
}

/**
 * Normalizes a section ID
 * @param {string} sectionId - The section ID
 * @returns {string} The normalized section ID
 */
export function normalizeSectionId(sectionId) {
  return sectionId.replace(new RegExp(`^${SECTION_ID_PREFIX}`), "");
}

/**
 * Checks if an element contains a shadow root
 * @param {Element} element - The element to check
 * @returns {boolean} Whether the element contains a shadow root
 */
function containsShadowRoot(element) {
  return (
    !!element.shadowRoot ||
    Array.from(element.children).some(containsShadowRoot)
  );
}

/**
 * @typedef {(previousElement: HTMLElement, newElement: HTMLElement) => void} UpdateCallback
 */

/**
 * Morphs the existing section element with the new section contents
 *
 * @param {string} sectionId - The section ID
 * @param {string} html - The new markup the section should morph into
 * @param {Object} [options] - Additional options
 * @param {boolean} [options.injectStylesheet=false] - When true, extracts `style[data-section-stylesheet]` from the response and injects it into the section wrapper.
 *
 * When the live section exists but the Section Rendering API response omits
 * it (common for layout-group sections such as global modals), no-op and
 * report via createReporter instead of throwing an unhandled storefront
 * error. Missing live sections still throw — that remains a caller bug.
 * Related: GH issue #609 / SHOPIFY-THEME-CE
 */
export async function morphSection(sectionId, html, options = {}) {
  const { injectStylesheet = false } = options;
  const fragment = new DOMParser().parseFromString(html, "text/html");
  const existingElement = document.getElementById(
    buildSectionSelector(sectionId),
  );
  const newElement = fragment.getElementById(buildSectionSelector(sectionId));

  if (!existingElement) {
    throw new Error(`Section ${sectionId} not found`);
  }

  if (!newElement) {
    reportError(
      new Error(
        `Section ${sectionId} not found in the section rendering response`,
      ),
      { tags: { op: "morph-section-missing-response" } },
    );
    return;
  }

  const island = existingElement.querySelector(
    "[data-morph-island]:not([hidden])",
  );
  if (island instanceof HTMLElement) {
    morphSectionPreservingIsland(existingElement, newElement, island);
    if (injectStylesheet) {
      injectSectionStylesheet(fragment, existingElement);
    }
    island.dispatchEvent(
      new CustomEvent("morph-island:dom-synced", { bubbles: true }),
    );
    return;
  }

  morph(existingElement, newElement, MORPH_OPTIONS);

  if (injectStylesheet) {
    injectSectionStylesheet(fragment, existingElement);
  }
}

/**
 * Park a live morph island, morph via the shared path, reinsert at its anchor.
 *
 * @param {Element} existingSection
 * @param {Element} newSection
 * @param {HTMLElement} island
 */
function morphSectionPreservingIsland(existingSection, newSection, island) {
  const owner = island.dataset.cartOwner;
  const incoming = owner
    ? newSection.querySelector(
        `[data-morph-island][data-cart-owner="${owner}"]`,
      )
    : newSection.querySelector("[data-morph-island]");
  incoming?.remove();

  island.dataset.morphPark = "true";
  const parking = document.createDocumentFragment();
  parking.appendChild(island);

  try {
    morph(existingSection, newSection, MORPH_OPTIONS);
  } finally {
    reinsertMorphIsland(existingSection, island);
  }
}

/**
 * Put a parked morph island back on the live section.
 *
 * @param {Element} existingSection
 * @param {HTMLElement} island
 */
function reinsertMorphIsland(existingSection, island) {
  if (island.isConnected) return;

  const selector = island.dataset.morphInsertBefore || ".cart-page__items";
  const page = existingSection.querySelector(".cart-page");
  const anchor =
    page instanceof HTMLElement ? page.querySelector(selector) : null;

  if (anchor) {
    anchor.parentNode?.insertBefore(island, anchor);
    return;
  }

  if (page instanceof HTMLElement) {
    page.appendChild(island);
    return;
  }

  console.warn(
    "[section-renderer] Morph island reinsert skipped: missing insert target",
  );
  existingSection.appendChild(island);
}

/**
 * Injects a `<style data-section-stylesheet>` from the parsed SFR response
 * into the live section wrapper. Replaces the existing stylesheet, if it exists.
 *
 * @param {Document} fragment - The parsed response document
 * @param {HTMLElement} sectionElement - The live section wrapper element
 */
function injectSectionStylesheet(fragment, sectionElement) {
  const newStylesheet = fragment.querySelector(
    "style[data-section-stylesheet]",
  );
  if (!newStylesheet) return;

  const existingStylesheet = sectionElement.querySelector(
    "style[data-section-stylesheet]",
  );

  if (existingStylesheet) {
    existingStylesheet.textContent = newStylesheet.textContent;
  } else {
    sectionElement.prepend(newStylesheet);
  }
}

export const sectionRenderer = new SectionRenderer();
